import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import manualPaymentVerificationService from '../../../services/manualPaymentVerificationService';
import { supabase } from '../../../lib/supabase';

const PaymentVerificationTable = ({ onVerificationComplete }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verifyingId, setVerifyingId] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState({
    amount: '',
    dateFrom: '',
    dateTo: '',
    reference: ''
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [bankStatementDate, setBankStatementDate] = useState('');

  useEffect(() => {
    loadPayments();

    // Subscribe to real-time payment updates
    const channel = supabase?.channel('payment-verification-updates')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payment_transactions',
          filter: 'payment_method=in.(bsp,kinabank,creditbank)'
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            // New payment added - reload list
            loadPayments();
          } else if (payload?.eventType === 'UPDATE') {
            // Payment updated - update in list
            setPayments(prev => 
              prev?.map(p => 
                p?.id === payload?.new?.id 
                  ? {
                      ...p,
                      reconciliationStatus: payload?.new?.reconciliation_status,
                      status: payload?.new?.status,
                      reconciledAt: payload?.new?.reconciled_at
                    }
                  : p
              )
            );
          } else if (payload?.eventType === 'DELETE') {
            // Payment deleted - remove from list
            setPayments(prev => prev?.filter(p => p?.id !== payload?.old?.id));
          }
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await manualPaymentVerificationService?.getPendingPaymentVerifications();
      setPayments(data);
    } catch (err) {
      setError(err?.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError('');
      const results = await manualPaymentVerificationService?.searchPayments(searchCriteria);
      setPayments(results);
    } catch (err) {
      setError(err?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPayment = async () => {
    if (!selectedPayment) return;

    try {
      setVerifyingId(selectedPayment?.id);
      setError('');

      await manualPaymentVerificationService?.verifyPayment(selectedPayment?.id, {
        bankReference,
        notes: verificationNotes,
        bankStatementDate
      });

      // Reset form
      setSelectedPayment(null);
      setVerificationNotes('');
      setBankReference('');
      setBankStatementDate('');

      // Reload data
      await loadPayments();
      onVerificationComplete?.();

      alert('Payment verified successfully!');
    } catch (err) {
      setError(err?.message || 'Verification failed');
    } finally {
      setVerifyingId(null);
    }
  };

  const getBankBadgeColor = (method) => {
    const colors = {
      bsp: 'bg-blue-100 text-blue-700',
      kinabank: 'bg-green-100 text-green-700',
      creditbank: 'bg-purple-100 text-purple-700'
    };
    return colors?.[method] || 'bg-gray-100 text-gray-700';
  };

  if (loading && payments?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Panel */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="Search" size={16} />
          Payment Matching System
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            type="number"
            placeholder="Amount (K)"
            value={searchCriteria?.amount}
            onChange={(e) => setSearchCriteria({ ...searchCriteria, amount: e?.target?.value })}
          />
          <Input
            type="text"
            placeholder="Reference Number"
            value={searchCriteria?.reference}
            onChange={(e) => setSearchCriteria({ ...searchCriteria, reference: e?.target?.value })}
          />
          <Input
            type="date"
            placeholder="Date From"
            value={searchCriteria?.dateFrom}
            onChange={(e) => setSearchCriteria({ ...searchCriteria, dateFrom: e?.target?.value })}
          />
          <Input
            type="date"
            placeholder="Date To"
            value={searchCriteria?.dateTo}
            onChange={(e) => setSearchCriteria({ ...searchCriteria, dateTo: e?.target?.value })}
          />
        </div>
        <Button onClick={handleSearch} className="w-full">
          <Icon name="Search" size={16} className="mr-2" />
          Search Payments
        </Button>
      </div>
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}
      {/* Payments Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Bank</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payments?.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No pending payment verifications
                  </td>
                </tr>
              ) : (
                payments?.map((payment) => (
                  <tr key={payment?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{payment?.companyName}</p>
                        <p className="text-xs text-muted-foreground">{payment?.contactEmail}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-bold text-foreground">K{payment?.amount?.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-bold rounded ${getBankBadgeColor(payment?.paymentMethod)}`}>
                        {payment?.paymentMethod?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-muted-foreground">
                        {payment?.transactionReference || payment?.receiptNumber || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-muted-foreground">
                        {new Date(payment?.createdAt)?.toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded">
                        {payment?.reconciliationStatus?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        onClick={() => setSelectedPayment(payment)}
                        disabled={verifyingId === payment?.id}
                      >
                        {verifyingId === payment?.id ? (
                          <Icon name="Loader2" size={14} className="animate-spin" />
                        ) : (
                          <>
                            <Icon name="CheckCircle" size={14} className="mr-1" />
                            Verify
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Verification Modal */}
      {selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">Verify Payment</h3>
              <button
                onClick={() => setSelectedPayment(null)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Payment Details */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Client:</span>
                  <span className="text-sm font-semibold text-foreground">{selectedPayment?.companyName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Amount:</span>
                  <span className="text-lg font-bold text-foreground">K{selectedPayment?.amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Bank:</span>
                  <span className={`px-2 py-1 text-xs font-bold rounded ${getBankBadgeColor(selectedPayment?.paymentMethod)}`}>
                    {selectedPayment?.paymentMethod?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Receipt Number:</span>
                  <span className="text-xs font-mono text-foreground">{selectedPayment?.receiptNumber}</span>
                </div>
              </div>

              {/* Verification Form */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Bank Reference Number</label>
                  <Input
                    type="text"
                    placeholder="Enter bank statement reference"
                    value={bankReference}
                    onChange={(e) => setBankReference(e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Bank Statement Date</label>
                  <Input
                    type="date"
                    value={bankStatementDate}
                    onChange={(e) => setBankStatementDate(e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Verification Notes</label>
                  <textarea
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    rows="3"
                    placeholder="Add verification notes (optional)"
                    value={verificationNotes}
                    onChange={(e) => setVerificationNotes(e?.target?.value)}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4">
                <Button
                  onClick={handleVerifyPayment}
                  disabled={verifyingId !== null}
                  className="flex-1"
                >
                  {verifyingId ? (
                    <>
                      <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Icon name="CheckCircle" size={16} className="mr-2" />
                      Confirm Verification
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSelectedPayment(null)}
                  disabled={verifyingId !== null}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentVerificationTable;